import { IMAGE_CDN } from "../config/config"

export function toCdnUrl(path?: string): string | undefined {
    if (!path) return undefined
    if (path.startsWith('http://') || path.startsWith('https://')) return path
    return `${IMAGE_CDN}/${path.replace(/^\//, '')}`
}
export const stripCdn = (url: string) => url.replace(`${IMAGE_CDN}/`, "");
export const resolveImage = (url: string) => {
    if (!url) return url;

    // If it is blob:, data:, or already a complete URL → return as is
    if (
        url.startsWith("blob:") ||
        url.startsWith("data:") ||
        url.startsWith("http://") ||
        url.startsWith("https://")
    ) {
        return url;
    }

    // Otherwise convert to CDN
    return toCdnUrl(url);
};

export const getCORSFetchUrl = (url: string): string => {
    if (!url) return url;
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    return url.includes("?") ? `${url}&cors=1` : `${url}?cors=1`;
};

/**
 * Formats a number in Indian locale (₹ convention):
 *   1000     → "1,000"
 *   10000    → "10,000"
 *   100000   → "1,00,000"
 *   1000000  → "10,00,000"
 * Always shows 2 decimal places.
 */
export const formatINR = (value: number | string | null | undefined): string => {
    const num = Number(value);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/**
 * Converts raw time strings (e.g., "14:30:00", "14:30", "9:30", ISO datetime, or "2:30 pm")
 * into standard 12-hour AM/PM format (e.g., "02:30 PM").
 * Suppresses "FULLDAY" or empty values by returning "".
 */
export const formatTimeToAMPM = (raw: string | null | undefined): string => {
    if (!raw) return "";
    const s = String(raw).trim();
    if (!s || s.toUpperCase().replace(/\s+/g, "") === "FULLDAY") return "";

    // If already formatted with AM/PM like "02:30 PM", "2:30 AM", or "2:30pm"
    const ampmMatch = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([aApP][mM])$/);
    if (ampmMatch) {
        let hours = parseInt(ampmMatch[1], 10);
        const mm = ampmMatch[2];
        const a = ampmMatch[3].toUpperCase();
        if (hours === 0) hours = 12;
        if (hours > 12) hours = hours % 12;
        return `${String(hours).padStart(2, "0")}:${mm} ${a}`;
    }

    // If 24-hour time like "14:30", "14:30:00", "9:30", "09:30:00"
    const time24Match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (time24Match) {
        let hours = parseInt(time24Match[1], 10);
        const mins = time24Match[2];
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        if (hours === 0) hours = 12;
        return `${String(hours).padStart(2, "0")}:${mins} ${ampm}`;
    }

    // If ISO datetime like "2026-09-08T14:30:00"
    if (s.includes("T")) {
        const timePart = s.split("T")[1];
        return formatTimeToAMPM(timePart);
    }

    return s;
};